/**
 * Wave 352-B · Navigation registry — single source of truth for SideNav.
 *
 * Every route the SideNav exposes lives here. Components do NOT
 * hardcode route literals — they reduce over `NAV_REGISTRY` via the
 * helpers below. When Wave 353 flips `obras: /projects → /campo`,
 * only this file changes; SideNav and the brand-link follow
 * automatically.
 *
 * The registry has exactly 24 entries (Wave 353 round-1 update):
 *   - 19 routes that already render in SideNav.
 *   - 3 Wave 352-B exposures (`/relatorios`, `/fornecedores`,
 *     `/notificacoes`).
 *   - 2 Wave 353 entries: `projects-list` (`/projects` reachable only
 *     via Mostrar tudo after the obras row flipped to `/campo`),
 *     and `hoje` (`/hoje` mobile-first queue, also Mostrar tudo on
 *     desktop since SideNav users already have `/inbox`).
 *
 * Forbidden `to:` values (negative-checked by lint:contracts):
 *   - `/admin` (AdminPage shell is admin-only; PM-visible
 *     `admin-usuarios-list` MUST point at `/admin/usuarios` so the
 *     route guard `<RequireAdminOrProjectManager>` doesn't bounce PMs
 *     away from their own user-management surface — Wave 355 R1B fix)
 *   - `/projects/:id/...` project sub-routes (project shell owns them)
 *   - public/auth/share routes (`/login`, `/register`,
 *     `/accept-invitation`, `/approve/:token`, `/c/:token`,
 *     `/o/:token`, `/compartilhado/...`, `/`)
 *
 * Why `mode` is required even on `placement: 'pinned'` / 'show_all_only'`
 * entries: Mostrar tudo groups by `legacyGroup`, not by `mode`, but a
 * future helper that needs "every entry conceptually under Gestão"
 * (e.g. cross-mode telemetry) reads from `mode`. Keeping it set on
 * every entry costs nothing and avoids a future schema fork.
 */

import type { UserRole } from '@builder/shared';

// ────────────────────────────── types ─────────────────────────────────

/**
 * Six work modes plus `tudo` reserved for "show all" tooling. The
 * mode rail renders icons for the first six; `tudo` is grouping
 * shorthand only.
 */
export type WorkMode =
  | 'campo'
  | 'inbox'
  | 'gestao'
  | 'financeiro'
  | 'qualidade'
  | 'sistema'
  | 'tudo';

/**
 * Mobile chrome policy. Wave 352-A locked the 4-tab crew strip and
 * 3-tab read-only strip; this field exists so future cross-surface
 * tools can ask "is this entry safe on a phone?" without re-reading
 * BottomTabs.tsx.
 */
export type MobilePolicy = 'hidden' | 'secondary' | 'primary';

/**
 * Where the entry renders in the SideNav chrome.
 *
 *  - `mode`           → Lives inside its `mode`'s contents column.
 *                       The common case for crew.
 *  - `pinned`         → Lives in the always-visible bottom block of
 *                       the rail, regardless of active mode (e.g.
 *                       Configurações, Central de ajuda, Usuários).
 *                       For client/broker, pinned is the WHOLE rail.
 *  - `show_all_only`  → Hidden from mode rail and pinned block;
 *                       reachable only via Mostrar tudo. Reserved
 *                       for legacy surfaces (e.g. `/notificacoes`).
 */
export type NavPlacement = 'mode' | 'pinned' | 'show_all_only';

/**
 * Pre-352-B grouping used by Mostrar tudo so the old mental model
 * survives. Entries with `legacyGroup === null` go in a fallback
 * "Outros" bucket.
 */
export type LegacyGroup =
  | 'visao-geral'
  | 'pendencias'
  | 'conta'
  | 'sistema'
  | 'mobile-only'
  | null;

export interface NavEntry {
  /** Stable ID — used for keys, telemetry, lint:contracts uniqueness. */
  id: string;
  /** i18n key. Most reuse existing `nav.<key>`; new ones live under `wave10.nav.entries.<id>`. */
  labelKey: string;
  /** Optional extended copy for Mostrar tudo descriptions / collapsed tooltips. */
  descriptionKey?: string;
  /** Exact route literal — must match a `path:` in router.tsx. */
  to: string;
  /** Primary mode this entry belongs to. Required even for `placement !== 'mode'` so Mostrar tudo can group. */
  mode: WorkMode;
  /** Where the entry renders. See `NavPlacement` jsdoc. */
  placement: NavPlacement;
  /** Roles this entry is visible to. Empty array → entry is hidden everywhere. */
  roles: UserRole[];
  /** Pre-352-B grouping for Mostrar tudo. */
  legacyGroup: LegacyGroup;
  /** Hard fence; admin-only regardless of `roles`. Sistema entries are always `true`. */
  adminOnly: boolean;
  /** Sort within `mode` or pinned block; lower number = higher in rail. */
  priority: number;
  /** Whether this entry is allowed on mobile primary chrome. Wave 352-A locked the strip. */
  mobilePolicy: MobilePolicy;
  /** Optional capability id from the Wave 347 brain registry; renders disabled when off. */
  requiresCapability?: string;
  /** Optional feature flag id; renders hidden when disabled. */
  featureFlag?: string;
}

// ─────────────────────────── the registry ─────────────────────────────

const ALL_CREW: UserRole[] = ['admin', 'project_manager', 'foreman', 'worker'];
const ALL_ROLES: UserRole[] = ['admin', 'project_manager', 'foreman', 'worker', 'client', 'broker'];
const ADMIN_ONLY: UserRole[] = ['admin'];
const ADMIN_PM: UserRole[] = ['admin', 'project_manager'];
const READ_ONLY: UserRole[] = ['client', 'broker'];
// W149 · 2026-05-28 · buyer + controller role groups.
const PROCUREMENT_ROLES: UserRole[] = ['admin', 'project_manager', 'buyer'];
const FINANCIAL_ROLES: UserRole[] = ['admin', 'project_manager', 'controller'];
// Wave nav-audit · 2026-05-20 · /broker is supplier-facing only.
// useBrokerOnlyRedirect (route-guards.tsx) bounces every crew role
// AND `client` away. Showing the row to anyone else creates a click
// that lands on /projects or /owner with no explanation — a concierge
// breakage. Restrict the sidebar entry to the audience the page
// actually serves; admins who need to QA still have the URL.
const BROKER_ONLY: UserRole[] = ['broker'];

/**
 * NAV_REGISTRY · the canonical 24-entry list (Wave 353 round 1).
 *
 * @deprecated W124.3 (2026-05-26) — Sidebar/BottomNav consumers MUST use
 * PM_NAV/ADMIN_NAV via `getNavForRole(role, projectId)`. This registry
 * is retained ONLY to back the few residual helpers that haven't been
 * migrated yet (`getPinnedNavEntriesForRole` for Settings/Ajuda pinned
 * rows, `getBrandHomeForRole` for brand link, and CommandBar deep links).
 * Do NOT add new entries here; add them to PM_NAV/ADMIN_NAV below.
 *
 * Adding a new entry (legacy/maintenance path only):
 *  1. Confirm the route is mounted in router.tsx.
 *  2. Decide `mode`, `placement`, `roles`, `legacyGroup`.
 *  3. Pick a unique `id` (lint:contracts enforces uniqueness).
 *  4. Update the WAVE-352-B doc + WAVE-353 doc § "Allowed entries".
 *  5. Update `scripts/check-nav-registry.mjs` allowed-list AND count.
 */
export const NAV_REGISTRY: readonly NavEntry[] = [
  // ─── Campo ─────────────────────────────────────────────────────────
  {
    id: 'obras',
    labelKey: 'nav.projects',
    // Wave 353 round 1 · `obras.to` is now `/campo`. This drives:
    //   - SideNav rail row (registry-rendered).
    //   - Brand/logo destination via `getBrandHomeForRole`.
    //   - RootRedirect (`/`) for crew via the same helper.
    // BottomTabs (mobile chrome) STILL routes the crew Campo tab
    // to `/projects` — that flip is deferred to a later round
    // alongside `isActiveTab` query-aware compat. The
    // `mobilePolicy: 'primary'` field below documents the
    // intended state, not the live BottomTabs `to:` value. Wave
    // 352-A's Campo tab `to:` literal is unchanged in round 1.
    to: '/campo',
    mode: 'campo',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 10,
    mobilePolicy: 'primary', // intended; BottomTabs flip lands in a later round
  },
  {
    // Wave 353 · legacy desktop project list. Reachable only via
    // Mostrar tudo so the SideNav rail stays clean. When users
    // need the full grid (filters, sort, bulk actions) they go
    // through Mostrar tudo → "Lista de obras".
    id: 'projects-list',
    labelKey: 'nav.projectsList',
    to: '/projects',
    mode: 'campo',
    placement: 'show_all_only',
    roles: ALL_CREW,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 15,
    mobilePolicy: 'hidden',
  },
  {
    id: 'atividade',
    labelKey: 'nav.activity',
    to: '/atividade',
    mode: 'campo',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 50,
    mobilePolicy: 'hidden',
  },
  // Trena · 2026-05-12 · entrada via sidebar pro orçamentador L2.
  // Rota top-level `/trena` cai num picker (TrenaPickerPage) que
  // pergunta qual obra antes de redirecionar pra
  // `/projects/:id/trena`. Só admin/PM (quem orça).
  {
    id: 'trena',
    labelKey: 'nav.trena',
    descriptionKey: 'nav.trenaDescription',
    to: '/trena',
    mode: 'campo',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 60,
    mobilePolicy: 'hidden',
  },

  // ─── Inbox ─────────────────────────────────────────────────────────
  {
    id: 'inbox',
    labelKey: 'nav.inbox',
    descriptionKey: 'nav.inboxDescription', // W148 · 2026-05-28
    to: '/inbox',
    mode: 'inbox',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 10,
    mobilePolicy: 'primary', // Wave 352-A crew Inbox tab
  },
  {
    id: 'alertas',
    labelKey: 'nav.alertas',
    descriptionKey: 'nav.alertasDescription', // W148 · 2026-05-28
    to: '/alertas',
    mode: 'inbox',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 20,
    mobilePolicy: 'hidden',
  },
  {
    id: 'anomalias',
    labelKey: 'nav.anomalias',
    descriptionKey: 'nav.anomaliasDescription', // W148 · 2026-05-28
    to: '/anomalias',
    mode: 'inbox',
    placement: 'mode',
    // W151 P1-7 · jargão de power-user (severidade/rule_code, ações que
    // só PM/admin executam). Foreman/worker não têm ação aqui — fora do
    // nav de crew. A própria página também usa useReadOnlyRedirect.
    roles: ADMIN_PM,
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 30,
    mobilePolicy: 'hidden',
  },
  {
    // Wave merge-inbox · 2026-05-20 · Concierge final #2.
    //
    // `/aprovacoes` foi absorvido pela Inbox unificada como sub-tab
    // (`/inbox?tab=aprovacoes`). O wrapper de página agora é só um
    // RedirectWithToast — bookmarks legados continuam vivos.
    //
    // Por que mantivemos a entry:
    //   - `check-nav-registry.mjs` exige que `/aprovacoes` esteja na
    //     allowed-to set e que tenha um `path:` resolvido em router.tsx.
    //     Remover a entry forçaria editar 3 lugares e re-aprovar o lint.
    //   - `roles: []` desliga visibilidade em TODAS as superfícies:
    //     `isVisibleTo` retorna `false` antes mesmo de checar capability.
    //     SideNav (mode + pinned + Mostrar tudo) e BottomTabs ignoram.
    //
    // Quando deletar: ~3 meses, junto do wrapper RedirectWithToast.
    //
    // WONTFIX até 2026-08-21 · entries com roles:[] mantidas pra check-nav-registry.mjs aceitar
    // os redirects /aprovacoes e /notificacoes (WAVE 22 merger). Quando 3+ meses sem hits em prod
    // nessas URLs (telemetria via funnel events), deletar wrappers + entries em uma wave de cleanup.
    id: 'aprovacoes',
    labelKey: 'nav.approvals',
    to: '/aprovacoes',
    mode: 'inbox',
    placement: 'mode',
    roles: [],
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 40,
    mobilePolicy: 'hidden',
  },
  {
    id: 'aprovacoes-usuarios',
    labelKey: 'nav.userApprovals',
    descriptionKey: 'nav.userApprovalsDescription', // W148 · 2026-05-28
    to: '/aprovacoes-usuarios',
    mode: 'inbox',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 50,
    mobilePolicy: 'hidden',
  },
  {
    // Wave merge-inbox · 2026-05-20 · Concierge final #2.
    //
    // `/notificacoes` foi absorvido pela Inbox unificada como sub-tab
    // (`/inbox?tab=avisos`). O wrapper de página agora é só um
    // RedirectWithToast — bookmarks legados continuam vivos.
    //
    // Por que mantivemos a entry: idem `aprovacoes` acima (lint:contracts
    // exige path resolvido em router.tsx + allowed-to set).
    //
    // BottomTabs mobile read-only ainda aponta direto pra `/notificacoes`
    // hoje. O redirect garante que ele cai no `/inbox?tab=avisos` com
    // banner de "Página renomeada" — aceitável como fallback até alguém
    // atualizar o BottomTabs no próximo wave de chrome mobile.
    //
    // Honest debt: o `mobilePolicy: 'secondary'` virou letra morta — a
    // tab read-only via mobile já não precisa desta entry pra navegar.
    //
    // WONTFIX até 2026-08-21 · entries com roles:[] mantidas pra check-nav-registry.mjs aceitar
    // os redirects /aprovacoes e /notificacoes (WAVE 22 merger). Quando 3+ meses sem hits em prod
    // nessas URLs (telemetria via funnel events), deletar wrappers + entries em uma wave de cleanup.
    id: 'notificacoes',
    labelKey: 'wave10.bottomTabs.avisos',
    to: '/notificacoes',
    mode: 'inbox',
    placement: 'show_all_only',
    roles: [],
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 60,
    mobilePolicy: 'secondary',
  },
  {
    // FEATURE B1 · H2.2 · 2026-05-21 — `/hoje` promoted to top-level for
    // PM/admin. The route dispatches in router.tsx (`HojeRouter`):
    //   admin/PM → HojeCrossObraPage (cross-obra hub, top-3 ranked actions)
    //   foreman/worker → HojePage (field-first queue, Wave 353 R3)
    // Both audiences share the same URL so HOME_BY_ROLE.project_manager
    // = '/hoje' (set in Wave home-pm-decidida) lands in the right view
    // without an extra redirect hop.
    //
    // Was `placement: 'show_all_only'` (Wave 353 R1) — flipped to `mode`
    // so the SideNav rail surfaces it inside the Inbox mode at priority
    // 5 (above /inbox itself). Foreman/worker still see the row but it
    // routes them to the legacy field queue.
    id: 'hoje',
    labelKey: 'wave10.bottomTabs.hoje',
    descriptionKey: 'nav.hojeDescription', // W148 · 2026-05-28
    to: '/hoje',
    mode: 'inbox',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'pendencias',
    adminOnly: false,
    priority: 5,
    mobilePolicy: 'primary', // BottomTabs Hoje tab routes here directly.
  },

  // ─── Gestão ────────────────────────────────────────────────────────
  {
    id: 'carteira',
    labelKey: 'nav.portfolio',
    to: '/carteira',
    mode: 'gestao',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 10,
    mobilePolicy: 'hidden',
  },
  {
    id: 'executivo',
    labelKey: 'nav.executivo',
    to: '/executivo',
    mode: 'gestao',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 20,
    mobilePolicy: 'hidden',
  },
  {
    // P1 fix · 2026-05-30 · Relatórios fragmentation — canonical decision.
    //
    // Two report surfaces coexist:
    //   - `/relatorios` (ReportsPage) — cross-project custom report builder
    //     + CSV export. This is the CANONICAL nav-level "Relatórios" entry.
    //   - `/projects/:id/relatorios` (RelatoriosPage) — the 3 canonical
    //     client-ready PDF reports, project-scoped. Owned by the project
    //     shell; nav-registry forbids `/projects/:id/...` `to:` values, so
    //     it is reached from inside a project, NOT from this rail row.
    //
    // The per-project PDF reports are therefore intentionally NOT a top-level
    // nav row. Instead, ReportsPage (`/relatorios`) carries an in-page bridge
    // card ("Relatórios prontos da obra (PDF) →") with a project picker that
    // routes to `projectRelatoriosPath(projectId)` — so the PM who clicks the
    // nav "Relatórios" expecting the client PDF is one pick away from it.
    // If a future wave adds a dedicated `/relatorios-obra` picker page
    // (mirroring DinheiroPickerPage), add a SECOND entry here pointing at that
    // top-level picker — never at a project sub-route.
    id: 'relatorios',
    labelKey: 'nav.relatorios',
    to: '/relatorios',
    mode: 'gestao',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 30,
    mobilePolicy: 'hidden',
  },
  {
    id: 'vitrine',
    labelKey: 'nav.ownerPortal',
    to: '/vitrine',
    mode: 'gestao',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 40,
    mobilePolicy: 'hidden',
  },

  // ─── Financeiro ────────────────────────────────────────────────────
  {
    id: 'fornecedores',
    labelKey: 'nav.fornecedores',
    to: '/fornecedores',
    mode: 'financeiro',
    placement: 'mode',
    roles: ALL_CREW,
    legacyGroup: null, // Outros bucket in Mostrar tudo
    adminOnly: false,
    priority: 10,
    mobilePolicy: 'hidden',
  },
  // LR-W3 · 2026-05-14 · Leilões reversos (admin PM).
  {
    id: 'admin-auctions',
    labelKey: 'nav.adminAuctions',
    to: '/admin/auctions',
    mode: 'financeiro',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: null,
    adminOnly: false,
    priority: 15,
    mobilePolicy: 'hidden',
  },
  // LR-W4 · 2026-05-15 · Broker / supplier-facing auction dashboard.
  // Wave nav-audit · 2026-05-20 · roles trimmed from ALL_ROLES → BROKER_ONLY.
  // Backend always 403s non-suppliers AND useBrokerOnlyRedirect (route-guards)
  // bounces every other role on hit; rendering this row for admin/PM/foreman/
  // worker/client gave them a Financeiro sidebar entry that instantly
  // redirected to /projects or /owner with no banner — a concierge failure.
  // Admins who need to QA the supplier UX still have the URL.
  {
    id: 'broker-dashboard',
    labelKey: 'nav.broker',
    to: '/broker',
    mode: 'financeiro',
    placement: 'mode',
    roles: BROKER_ONLY,
    legacyGroup: null,
    adminOnly: false,
    priority: 16,
    mobilePolicy: 'hidden',
  },
  {
    id: 'broker-history',
    labelKey: 'nav.broker',
    to: '/broker/history',
    mode: 'financeiro',
    placement: 'show_all_only',
    roles: BROKER_ONLY,
    legacyGroup: null,
    adminOnly: false,
    priority: 17,
    mobilePolicy: 'hidden',
  },
  // LR-W2 · #51 · 2026-05-11 · Recomendador de Modalidade (shadow).
  // Entrada visível pra admin+PM dentro do modo Financeiro · usuário
  // entra direto sem ter de saber a URL admin.
  {
    id: 'procurement-cockpit',
    labelKey: 'nav.procurementCockpit',
    to: '/admin/procurement-cockpit',
    mode: 'financeiro',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: null,
    adminOnly: false,
    priority: 20,
    mobilePolicy: 'hidden',
  },
  // LR-W2 · #51 · 2026-05-11 · Contratos guarda-chuva (framework contracts).
  {
    id: 'procurement-frameworks',
    labelKey: 'nav.procurementFrameworks',
    to: '/admin/procurement-frameworks',
    mode: 'financeiro',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: null,
    adminOnly: false,
    priority: 30,
    mobilePolicy: 'hidden',
  },
  // LR-W2 · #56 · 2026-05-11 · Painel cross-obras de compras (KPIs + savings).
  {
    id: 'procurement-dashboard',
    labelKey: 'nav.procurementDashboard',
    to: '/admin/procurement-dashboard',
    mode: 'financeiro',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: null,
    adminOnly: false,
    priority: 5,
    mobilePolicy: 'hidden',
  },

  // P1 fix · 2026-05-27 · Mesa Dinheiro (gastos da obra) surfaced in
  // Financeiro mode. Routes to /dinheiro top-level picker which asks
  // "qual obra?" before redirecting to /projects/:id/dinheiro.
  // priority: 1 → first entry in Financeiro so PM sees it immediately.
  {
    id: 'mesa-dinheiro',
    labelKey: 'nav.dinheiro',
    to: '/dinheiro',
    mode: 'financeiro',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: null,
    adminOnly: false,
    priority: 1,
    mobilePolicy: 'hidden',
  },
  // W149 · 2026-05-28 · Compras (todas as obras) — landing pra buyer.
  // buyer entra aqui via accept-invitation redirect. Admin/PM também
  // acessam como atalho cross-obra de compras.
  {
    id: 'compras-geral',
    labelKey: 'nav.comprasGeral',
    descriptionKey: 'nav.comprasGeralDescription',
    to: '/compras-geral',
    mode: 'financeiro',
    placement: 'mode',
    roles: PROCUREMENT_ROLES,
    legacyGroup: null,
    adminOnly: false,
    priority: 2,
    mobilePolicy: 'hidden',
  },
  // W149 · 2026-05-28 · Dinheiro (todas as obras) — landing pra controller.
  // controller entra aqui via accept-invitation redirect. Admin/PM também
  // acessam como atalho cross-obra financeiro.
  {
    id: 'dinheiro-geral',
    labelKey: 'nav.dinheiroGeral',
    descriptionKey: 'nav.dinheiroGeralDescription',
    to: '/dinheiro-geral',
    mode: 'financeiro',
    placement: 'mode',
    roles: FINANCIAL_ROLES,
    legacyGroup: null,
    adminOnly: false,
    priority: 3,
    mobilePolicy: 'hidden',
  },

  // ─── Qualidade ─────────────────────────────────────────────────────
  // Wave UUUU · 2026-05-16 · Top-level cross-project quality hub.
  // Admin + PM: see the full portfolio view. Foreman/worker access
  // per-project FVS/RNC from the project shell (not top-level).
  {
    id: 'qualidade-hub',
    labelKey: 'nav.qualidadeHub',
    to: '/qualidade',
    mode: 'qualidade',
    placement: 'mode',
    roles: ADMIN_PM,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 10,
    mobilePolicy: 'hidden',
  },

  // ─── Sistema (admin only) ──────────────────────────────────────────
  // Wave ggggg · 2026-05-16 · Admin home — tile rollup of all admin surfaces.
  // Admin-only; priority 1 so it floats to the very top of Sistema mode.
  // Forbidden-to-nav check updated in check-nav-registry.mjs to allow '/admin'
  // for ADMIN_ONLY entries (PMs never see this row so the bounce-guard concern
  // from Wave 355 R1B does not apply here).
  {
    id: 'admin-home',
    labelKey: 'nav.adminHome',
    to: '/admin',
    mode: 'sistema',
    placement: 'mode',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 1,
    mobilePolicy: 'hidden',
  },
  // Wave 393A · 2026-05-15 · Admin override audit history (Trena).
  // Admin-only: surfaces admin_override_commit entries written to
  // estimates.sanity_warnings during forced commits.
  {
    id: 'admin-trena-overrides',
    labelKey: 'nav.adminTrenaOverrides',
    to: '/admin/trena/overrides',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 55,
    mobilePolicy: 'hidden',
  },
  // Wave 392 · 2026-05-15 · Admin Trena R2 storage stats.
  // Admin-only: bucket object count, total bytes, orphan count, truncation flag.
  {
    id: 'admin-trena-storage',
    labelKey: 'nav.adminTrenaStorage',
    to: '/admin/trena/storage',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 56,
    mobilePolicy: 'hidden',
  },
  // Wave 393B · 2026-05-15 · Admin Trena compositions compat audit.
  // Admin-only: surfaces required_measurement_keys / blocked_measurement_keys
  // compat lists and drift detection against MEASURE_POLICIES.
  {
    id: 'admin-trena-compositions',
    labelKey: 'nav.adminTrenaCompositions',
    to: '/admin/trena/compositions',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 57,
    mobilePolicy: 'hidden',
  },
  // Wave QQQ · 2026-05-16 · Admin follow-ups telemetry.
  // Admin-only: read surface over follow_ups_audit (cron fires every 15 min).
  {
    id: 'admin-follow-ups',
    labelKey: 'nav.adminFollowUps',
    to: '/admin/follow-ups',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 58,
    mobilePolicy: 'hidden',
  },
  // Wave 379AI · Command & Control entra como entrada PRIMÁRIA do
  // modo Sistema. Consolida ~41 páginas admin em Kanban acionável.
  // Os items legacy continuam acessíveis por URL + abaixo do C&C, mas
  // o caminho default agora é uma tela só.
  {
    id: 'admin-command-control',
    labelKey: 'nav.adminCommandControl',
    to: '/admin/command-control',
    mode: 'sistema',
    placement: 'mode',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 5,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-brain',
    labelKey: 'nav.adminBrain',
    to: '/admin/brain',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 10,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-saude',
    labelKey: 'nav.adminSaude',
    to: '/admin/saude',
    mode: 'sistema',
    placement: 'mode',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 20,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-deploys',
    labelKey: 'nav.adminDeploys',
    to: '/admin/deploys',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 30,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomia',
    labelKey: 'nav.adminAutonomia',
    to: '/admin/autonomia',
    mode: 'sistema',
    placement: 'mode',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 40,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-conectores',
    labelKey: 'nav.adminConectores',
    to: '/admin/conectores',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 50,
    mobilePolicy: 'hidden',
  },

  // ─── Autonomy sub-routes (admin-only) ──────────────────────────────
  {
    id: 'admin-autonomy-incidents',
    labelKey: 'nav.adminAutonomyIncidents',
    to: '/admin/autonomy/incidents',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 100,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-pm-loop',
    labelKey: 'nav.adminAutonomyPMLoop',
    to: '/admin/autonomy/pm-loop',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 101,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-data-self-heal',
    labelKey: 'nav.adminAutonomyDataSelfHeal',
    to: '/admin/autonomy/data-self-heal',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 102,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-workflow-self-heal',
    labelKey: 'nav.adminAutonomyWorkflowSelfHeal',
    to: '/admin/autonomy/workflow-self-heal',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 103,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-procurement-autopilot',
    labelKey: 'nav.adminAutonomyProcurementAutopilot',
    to: '/admin/autonomy/procurement-autopilot',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 104,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-resource-optimizer',
    labelKey: 'nav.adminAutonomyResourceOptimizer',
    to: '/admin/autonomy/resource-optimizer',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 105,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-rule-autopilot',
    labelKey: 'nav.adminAutonomyRuleAutopilot',
    to: '/admin/autonomy/rule-autopilot',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 106,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-quality-autopilot',
    labelKey: 'nav.adminAutonomyQualityAutopilot',
    to: '/admin/autonomy/quality-autopilot',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 107,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-claims-detection',
    labelKey: 'nav.adminAutonomyClaimsDetection',
    to: '/admin/autonomy/claims-detection',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 108,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-owner-trust-plus',
    labelKey: 'nav.adminAutonomyOwnerTrustPlus',
    to: '/admin/autonomy/owner-trust-plus',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 109,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-agent-governor',
    labelKey: 'nav.adminAutonomyAgentGovernor',
    to: '/admin/autonomy/agent-governor',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 110,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-self-tuning',
    labelKey: 'nav.adminAutonomySelfTuning',
    to: '/admin/autonomy/self-tuning',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 111,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-daily-brief',
    labelKey: 'nav.adminAutonomyDailyBrief',
    to: '/admin/autonomy/daily-brief',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 112,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-sla',
    labelKey: 'nav.adminAutonomySLA',
    to: '/admin/autonomy/sla',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 113,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-radar-critic',
    labelKey: 'nav.adminAutonomyRadarCritic',
    to: '/admin/autonomy/radar/critic',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 114,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-chaos',
    labelKey: 'nav.adminAutonomyChaos',
    to: '/admin/autonomy/chaos',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 115,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-autonomy-exceptions-only',
    labelKey: 'nav.adminAutonomyExceptionsOnly',
    to: '/admin/autonomy/exceptions-only',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 116,
    mobilePolicy: 'hidden',
  },

  // ─── Financial & Compliance (admin-only) ───────────────────────────
  {
    id: 'admin-monthly-close',
    labelKey: 'nav.adminMonthlyClose',
    to: '/admin/monthly-close',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 200,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-financial-risk',
    labelKey: 'nav.adminFinancialRisk',
    to: '/admin/financial-risk',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 201,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-audit-pack',
    labelKey: 'nav.adminAuditPack',
    to: '/admin/audit-pack',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 202,
    mobilePolicy: 'hidden',
  },

  // ─── Observability & Insights (admin-only) ─────────────────────────
  {
    id: 'admin-memory-graph',
    labelKey: 'nav.adminMemoryGraph',
    to: '/admin/observability/memory-graph',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 300,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-benchmarking',
    labelKey: 'nav.adminBenchmarking',
    to: '/admin/benchmarking',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 301,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-sop',
    labelKey: 'nav.adminSOP',
    to: '/admin/sop',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 302,
    mobilePolicy: 'hidden',
  },

  // ─── Tooling (admin-only) ─────────────────────────────────────────
  {
    id: 'admin-policy',
    labelKey: 'nav.adminPolicy',
    to: '/admin/policy',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 400,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-simulation',
    labelKey: 'nav.adminSimulation',
    to: '/admin/simulation',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 401,
    mobilePolicy: 'hidden',
  },
  // ─── W383 R8/R9 · product analytics + decision graph + Housy unknowns
  {
    id: 'admin-product-analytics',
    labelKey: 'nav.adminProductAnalytics',
    to: '/admin/product-analytics',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 410,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-decision-graph',
    labelKey: 'nav.adminDecisionGraph',
    to: '/admin/observability/decision-graph',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 411,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-housy-unknowns',
    labelKey: 'nav.adminHousyUnknowns',
    to: '/admin/housy/unknowns',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 412,
    mobilePolicy: 'hidden',
  },
  // ─── Tier F · platform admin ───────────────────────────────────────
  {
    id: 'admin-tenants',
    labelKey: 'nav.adminTenants',
    to: '/admin/tenants',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 420,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-bulk-ops',
    labelKey: 'nav.adminBulkOps',
    to: '/admin/bulk-ops',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 421,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-audit-log',
    labelKey: 'nav.adminAuditLog',
    to: '/admin/audit-log',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 422,
    mobilePolicy: 'hidden',
  },
  {
    // Error tracking · 2026-05-20 — CF-native observability dashboard.
    id: 'admin-observability',
    labelKey: 'nav.adminObservability',
    to: '/admin/observability',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 425,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-backups',
    labelKey: 'nav.adminBackups',
    to: '/admin/backups',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 423,
    mobilePolicy: 'hidden',
  },

  // Wave 41 · 2026-05-23 · Meta WhatsApp template watcher (admin-only).
  {
    id: 'admin-meta-templates',
    labelKey: 'nav.adminMetaTemplates',
    to: '/admin/meta-templates',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 426,
    mobilePolicy: 'hidden',
  },
  // Wave 47 · 2026-05-23 · Generative Engineering Labs (admin-only demo).
  {
    id: 'admin-labs-generative',
    labelKey: 'nav.adminLabsGenerative',
    to: '/admin/labs/generative',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 427,
    mobilePolicy: 'hidden',
  },
  // Wave 67 F2.4 · 2026-05-23 · Generative Lab history (admin-only).
  {
    id: 'admin-labs-generative-history',
    labelKey: 'nav.adminLabsGenerativeHistory',
    to: '/admin/labs/generative/historico',
    mode: 'sistema',
    placement: 'show_all_only',
    roles: ADMIN_ONLY,
    legacyGroup: 'sistema',
    adminOnly: true,
    priority: 428,
    mobilePolicy: 'hidden',
  },

  // ─── Pinned Conta block ────────────────────────────────────────────
  // L2 · 2026-06-03 · entry legada `settings` removida. SideNavHierarchical
  // (rail vivo) já renderiza "Configurações" via PM_NAV `config` group no
  // bloco principal; manter também a pinned legada fazia "Configurações"
  // aparecer DUAS vezes por role. MobileBottomNav usa PM_NAV `config` ("Mais"
  // overlay), não a pinned. pinnedCount continua >= 3 (ajuda +
  // admin-usuarios-list + owner) → check-nav-registry.mjs Check 11 ok.
  {
    // Wave pm-uom · 2026-05-17 · Unidades de medida customizadas.
    // Só PM/admin (quem orça); show_all_only pra não poluir o rail.
    id: 'settings-uom',
    labelKey: 'nav.settingsUom',
    to: '/settings/unidades',
    mode: 'gestao',
    placement: 'show_all_only',
    roles: ADMIN_PM,
    legacyGroup: 'conta',
    adminOnly: false,
    priority: 15,
    mobilePolicy: 'hidden',
  },
  {
    id: 'ajuda',
    labelKey: 'nav.ajuda',
    to: '/ajuda',
    mode: 'gestao',
    placement: 'pinned',
    roles: ALL_ROLES,
    legacyGroup: 'conta',
    adminOnly: false,
    priority: 20,
    mobilePolicy: 'hidden',
  },
  {
    id: 'admin-usuarios-list',
    // Wave 355 R1B · routes directly to `/admin/usuarios` (the
    // AdminUsersPage), not the AdminPage shell at `/admin`. R1
    // tightened the route guards: `/admin` is now `<RequireAdmin>`
    // (admin-only) and `/admin/usuarios` is wrapped in
    // `<RequireAdminOrProjectManager>` (admin + PM by product rule —
    // backend `requireRole('admin','project_manager')` +
    // `canActorManage` tier in admin-users.service.ts). Pointing this
    // pinned entry at `/admin` would have bounced PMs away from the
    // very surface they're allowed to use. Kept under pinned Conta —
    // NOT Sistema — so the Sistema admin-only rule has no exceptions.
    labelKey: 'nav.adminUsuarios',
    to: '/admin/usuarios',
    mode: 'gestao',
    placement: 'pinned',
    roles: ADMIN_PM,
    legacyGroup: 'conta',
    adminOnly: false,
    priority: 30,
    mobilePolicy: 'hidden',
  },

  // ─── Read-only Owner surface ───────────────────────────────────────
  {
    id: 'owner',
    labelKey: 'nav.myProjects',
    to: '/owner',
    mode: 'gestao', // for grouping; read-only viewers don't see Mostrar tudo anyway
    placement: 'pinned',
    roles: READ_ONLY,
    legacyGroup: 'visao-geral',
    adminOnly: false,
    priority: 5, // above settings/ajuda for read-only viewers
    mobilePolicy: 'primary', // Wave 352-A read-only Owner tab
  },
];

// ─────────────────────────────── helpers ──────────────────────────────

/**
 * Whether `entry` is visible to `role`.
 *
 * Rules (locked by lint:contracts):
 *  - `adminOnly: true` is a hard fence — only `admin` ever sees it.
 *  - Otherwise, role must appear in `entry.roles`.
 *  - `featureFlag` and `requiresCapability` further gate visibility
 *    via `capabilityState`. Unknown / off capability state: entry
 *    stays in the registry but renders disabled (NOT hidden); the
 *    SideNav layer handles that. The helpers themselves treat
 *    capability as visibility-only IFF the capability is explicitly
 *    'off' — 'on' and 'unknown' both flow through.
 */
function isVisibleTo(
  entry: NavEntry,
  role: UserRole,
  capabilityState?: ReadonlyMap<string, 'on' | 'off' | 'unknown'>,
): boolean {
  if (entry.adminOnly && role !== 'admin') return false;
  if (!entry.roles.includes(role)) return false;
  if (entry.requiresCapability && capabilityState) {
    const state = capabilityState.get(entry.requiresCapability);
    if (state === 'off') return false;
  }
  return true;
}

/**
 * Default mode landing per role.
 *
 *   admin / project_manager  → 'inbox' (action-first chrome)
 *   foreman / worker         → 'campo' (field-first chrome)
 *   client / broker          → 'tudo'  (no mode rail; pinned-only;
 *                              the value is symbolic since SideNav
 *                              short-circuits read-only into
 *                              pinned-only rendering)
 */
export function getDefaultModeForRole(role: UserRole): WorkMode {
  if (role === 'admin' || role === 'project_manager') return 'inbox';
  if (role === 'foreman' || role === 'worker') return 'campo';
  if (role === 'buyer' || role === 'controller') return 'financeiro'; // W149
  return 'tudo';
}

/**
 * Entries inside the active mode's contents column.
 * Filters by role + `placement === 'mode'` + `mode === <active>`.
 * Sorted by `priority` ascending.
 */
export function getModeEntriesForRole(
  role: UserRole,
  mode: WorkMode,
  capabilityState?: ReadonlyMap<string, 'on' | 'off' | 'unknown'>,
): NavEntry[] {
  return NAV_REGISTRY
    .filter((e) => e.placement === 'mode')
    .filter((e) => e.mode === mode)
    .filter((e) => isVisibleTo(e, role, capabilityState))
    .slice()
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Entries in the always-visible pinned bottom block.
 * Crew sees Conta pinned (settings/ajuda/admin-usuarios-list).
 * Read-only sees owner + settings/ajuda — those are their WHOLE rail.
 * Sorted by `priority` ascending.
 */
export function getPinnedNavEntriesForRole(
  role: UserRole,
  capabilityState?: ReadonlyMap<string, 'on' | 'off' | 'unknown'>,
): NavEntry[] {
  return NAV_REGISTRY
    .filter((e) => e.placement === 'pinned')
    .filter((e) => isVisibleTo(e, role, capabilityState))
    .slice()
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Every entry the role can reach, regardless of placement. Drives
 * Mostrar tudo. Includes mode + pinned + show_all_only.
 */
export function getAllNavEntriesForRole(
  role: UserRole,
  capabilityState?: ReadonlyMap<string, 'on' | 'off' | 'unknown'>,
): NavEntry[] {
  return NAV_REGISTRY
    .filter((e) => isVisibleTo(e, role, capabilityState))
    .slice()
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Group entries by `legacyGroup` for Mostrar tudo. `null` → "outros".
 * Returned object iteration order is insertion order, which matches
 * the order entries appear in `entries` (caller controls priority).
 */
export function groupEntriesByLegacyGroup(
  entries: NavEntry[],
): Record<string, NavEntry[]> {
  const out: Record<string, NavEntry[]> = {};
  for (const entry of entries) {
    const key = entry.legacyGroup ?? 'outros';
    if (!out[key]) out[key] = [];
    out[key]!.push(entry);
  }
  return out;
}

/**
 * Wave 352-B round-6 · pure resolver for the SideNav's initial
 * active mode. Centralizes the priority order so SideNav.tsx never
 * needs to second-guess the rules across hydration boundaries.
 *
 * Priority:
 *   1. read-only roles (`client` / `broker`) → `tudo` (symbolic;
 *      SideNav short-circuits read-only into pinned-only render).
 *   2. valid + role-visible stored mode → use it.
 *      Visible means `isModeVisibleForRole`-style: Sistema only for
 *      admin; Qualidade always (empty-state CTA path); other modes
 *      iff `getModeEntriesForRole(role, mode)` is non-empty.
 *   3. fallback → `getDefaultModeForRole(role)`.
 *
 * Why this lives here instead of in SideNav: the SideNav's
 * `useState` initializer fires before async auth hydration, so it
 * could land on `inbox` for a session that should be `campo`. By
 * making this a pure function, SideNav can re-run resolution on
 * the role-change effect cheaply and deterministically.
 */
export function resolveInitialModeForRole(
  role: UserRole,
  storedMode: string | null,
  capabilityState?: ReadonlyMap<string, 'on' | 'off' | 'unknown'>,
): WorkMode {
  if (role === 'client' || role === 'broker') return 'tudo';
  // W149 · buyer/controller have entries only in financeiro mode.
  if (role === 'buyer' || role === 'controller') return 'financeiro';

  const VALID_RAIL: WorkMode[] = [
    'campo',
    'inbox',
    'gestao',
    'financeiro',
    'qualidade',
    'sistema',
  ];

  const isVisible = (mode: WorkMode): boolean => {
    if (mode === 'sistema') return role === 'admin';
    if (mode === 'qualidade') return true; // empty-state CTA path
    return getModeEntriesForRole(role, mode, capabilityState).length > 0;
  };

  if (
    storedMode &&
    (VALID_RAIL as string[]).includes(storedMode) &&
    isVisible(storedMode as WorkMode)
  ) {
    return storedMode as WorkMode;
  }

  return getDefaultModeForRole(role);
}

/**
 * Wave home-pm-decidida · HOME_BY_ROLE mapping (canonical, 2026-05-17).
 *
 * Decision (JP + OPERATING-MANUAL §3 "operação guiada"):
 *   admin           → /projects (operacional; ROUTE-01 2026-06-03 flipou
 *                     de /comando, que ainda é acessível via ADMIN_NAV)
 *   project_manager → /hoje     (fila do dia cross-obras, operação guiada)
 *   foreman/worker  → /campo    (campo-first, registro de RDO/FVS)
 *   client          → /owner    (painel do proprietário, leitura)
 *   broker          → /broker   (painel do fornecedor/corretor)
 *
 * Eliminates the ping-pong: antes /campo redirecionava admin/PM pra
 * /projects → que mandava de volta. Agora cada role tem home própria.
 *
 * CampoPage.tsx não redireciona mais admin/PM — campo continua
 * acessível mas não é o destino padrão de admin/PM.
 *
 * (lint:contracts: function name `getBrandHomeForRole` preserved.)
 */

/** HOME_BY_ROLE — mapeamento canônico de role → rota home. */
export const HOME_BY_ROLE: Record<UserRole, string> = {
  // ROUTE-01 · 2026-06-03 · admin home agora é operacional (/projects),
  // não o NOC (/comando). /comando segue acessível via ADMIN_NAV
  // (entry 'comando' → /admin/command-control). Alinha com
  // LoginPage.destFor (admin/PM → /projects) e elimina o desencontro
  // entre os dois resolvers.
  admin:           '/projects',
  project_manager: '/hoje',
  foreman:         '/campo',
  worker:          '/campo',
  client:          '/owner',
  broker:          '/broker',
  buyer:           '/compras-geral', // W149
  controller:      '/dinheiro-geral', // W149
};

/**
 * Wave 352-B round-4 final constraint · brand/logo destination must
 * come from the registry, NOT a hardcoded literal in SideNav.tsx.
 *
 * Wave home-pm-decidida · 2026-05-17 — updated role mapping.
 * ROUTE-01 · 2026-06-03 — admin flipped /comando → /projects:
 *   admin           → /projects (operacional; concorda com RootRedirect
 *                     + LoginPage.destFor. /comando segue acessível via
 *                     ADMIN_NAV entry 'comando')
 *   project_manager → /hoje     (fila do dia, operação guiada)
 *   foreman/worker  → obras entry's `to` (/campo, registry-tracked)
 *   client/broker   → owner entry's `to` (/owner, registry-tracked)
 *
 * lint:contracts (check-nav-registry.mjs check-12) requires:
 *   - id === 'owner' lookup for client/broker
 *   - id === 'obras' lookup for foreman/worker
 *   - all 6 role literals explicitly present in the body
 *   - no getModeEntriesForRole / getDefaultModeForRole calls
 */
export function getBrandHomeForRole(role: UserRole): string {
  // client → registry-tracked owner entry (currently /owner).
  if (role === 'client') {
    const owner = NAV_REGISTRY.find((e) => e.id === 'owner');
    return owner ? owner.to : '/';
  }
  // broker → supplier/broker dashboard (wave home-pm-decidida).
  if (role === 'broker') {
    const owner = NAV_REGISTRY.find((e) => e.id === 'owner');
    // broker-dashboard entry in registry if needed; else hardcoded.
    const brokerEntry = NAV_REGISTRY.find((e) => e.id === 'broker-dashboard');
    return brokerEntry ? brokerEntry.to : (owner ? '/broker' : '/');
  }
  // Wave home-pm-decidida · admin/PM get guided-operation surfaces,
  // not the field crew surface (/campo). No more ping-pong.
  // ROUTE-01 · 2026-06-03 · admin agora cai em /projects (operacional),
  // não no NOC /comando — concorda com RootRedirect e LoginPage.destFor.
  // /comando segue alcançável pela ADMIN_NAV (entry 'comando').
  if (role === 'admin') {
    return '/projects';
  }
  if (role === 'project_manager') {
    return '/hoje';
  }
  // foreman/worker → registry-tracked obras entry (currently /campo).
  // When obras.to changes, the home link follows for free.
  if (role === 'foreman' || role === 'worker') {
    const obras = NAV_REGISTRY.find((e) => e.id === 'obras');
    return obras ? obras.to : '/';
  }
  // W149 · buyer/controller → their cross-obra landing pages.
  if (role === 'buyer') return '/compras-geral';
  if (role === 'controller') return '/dinheiro-geral';
  return '/';
}

// ─────────────────────────────────────────────────────────────────────
// W123.4 · 2026-05-26 · New 6-group + Configurações PM navigation.
//
// JP cravou em HANDOFF-2026-05-25-REFACTOR-UX-W122 P1 / W123 4.2 que
// 20+ módulos espalhados pelo Builder viram 6 grupos + 1 Configurações,
// com Admin segregada (só role=admin).
//
// Esta seção EXISTE EM PARALELO ao NAV_REGISTRY acima. NAV_REGISTRY
// segue sendo a fonte de truth pra SideNav legado / lint:contracts /
// helpers de capability. PM_NAV/ADMIN_NAV são o NOVO modelo de grupos
// que SideNavHierarchical (W123.5+) vai consumir conforme as mesas
// de trabalho (`/plano`, `/obra`, `/dinheiro`) forem entrando.
//
// Regras inquebráveis (CLAUDE.md):
//   - NÃO usa `/admin` hardcoded em `to:` literais. Cada entry é uma
//     função `(projectId?) => string`. lint:contracts proíbe literais.
//   - Housy/Prumo NÃO aparecem como nome de produto pro PM. Substituídos
//     por "Ajuda" (AjudaContextual drawer) e "Próxima ação" (ProximaAcaoCard).
//   - Admin só renderiza se role==='admin'. PM nunca vê /admin/*.
// ─────────────────────────────────────────────────────────────────────

/** Grupo de navegação PM/Admin (W123.4). */
export interface NavGroup {
  /** Id estável pra React keys, telemetria, lint. */
  id: string;
  /** i18n key (em pt-BR.ts/en.ts/types.ts em `nav.<key>`). */
  labelKey: string;
  /** Lucide icon name (string), resolvido no SideNav. */
  icon: string;
  /**
   * Resolver da rota. Recebe o projectId atual (string vazia se não
   * houver). Grupos cross-obra ignoram o argumento.
   */
  to: (projectId?: string) => string;
  /**
   * Quando true, o grupo só faz sentido com projectId — sidebar deve
   * renderizar disabled / link pra picker quando estiver fora de obra.
   */
  requiresProject?: boolean;
}

/**
 * PM_NAV · 6 grupos + Configurações (ordem cravada por JP).
 *
 * Hoje aceita projectId opcional: dentro de obra → `/projects/:id/hoje`;
 * fora de obra → `/hoje` cross-obra hub.
 *
 * Os outros 5 grupos PM (Plano, Compras, Obra, Dinheiro, Entrega) são
 * por-obra (`requiresProject: true`). Configurações é cross-obra.
 */
export const PM_NAV: readonly NavGroup[] = [
  {
    id: 'hoje',
    labelKey: 'nav.hoje',
    icon: 'Home',
    to: (projectId) => (projectId ? `/projects/${projectId}/hoje` : '/hoje'),
  },
  {
    id: 'plano',
    labelKey: 'nav.plano',
    icon: 'FileText',
    to: (projectId) => `/projects/${projectId ?? ''}/plano`,
    requiresProject: true,
  },
  {
    id: 'compras',
    labelKey: 'nav.compras',
    icon: 'ShoppingCart',
    to: (projectId) => `/projects/${projectId ?? ''}/compras`,
    requiresProject: true,
  },
  {
    id: 'obra',
    labelKey: 'nav.obra',
    icon: 'HardHat',
    to: (projectId) => `/projects/${projectId ?? ''}/obra`,
    requiresProject: true,
  },
  {
    id: 'dinheiro',
    labelKey: 'nav.dinheiro',
    icon: 'Wallet',
    to: (projectId) => `/projects/${projectId ?? ''}/dinheiro`,
    requiresProject: true,
    // Controle de materiais (Estoque) também vive aqui:
    // `/projects/:id/dinheiro?view=estoque`
  },
  {
    id: 'checklist',
    labelKey: 'nav.checklist',
    icon: 'ClipboardCheck',
    to: (projectId) => `/projects/${projectId ?? ''}/checklist`,
    requiresProject: true,
  },
  {
    id: 'entrega',
    labelKey: 'nav.entrega',
    icon: 'Package',
    to: (projectId) => `/projects/${projectId ?? ''}/entrega`,
    requiresProject: true,
  },
  {
    id: 'config',
    labelKey: 'nav.config',
    icon: 'Settings',
    to: () => '/settings',
  },
];

/**
 * Path canônico dos relatórios-PDF prontos da obra (project-scoped).
 *
 * Os 3 relatórios prontos pro cliente (boletim mensal, fluxo de caixa,
 * status pro cliente) vivem em `/projects/:id/relatorios` (RelatoriosPage),
 * dentro do contexto da obra. A nav top-level `/relatorios` (ReportsPage)
 * é o builder de exportação cross-obra — conceito diferente. ReportsPage
 * usa este helper pra oferecer a ponte "PDF pronto da obra →" sem
 * hardcodear a rota. Fonte única da verdade pra esse destino.
 */
export function projectRelatoriosPath(projectId: string): string {
  return `/projects/${projectId}/relatorios`;
}

/**
 * CREW_NAV · subconjunto reduzido pra foreman/worker (W151 P0-3).
 *
 * Antes, `getNavForRole` devolvia PM_NAV inteiro pra qualquer role !=
 * admin — o mestre/pedreiro saía do Campo e via Plano (orçamento),
 * Compras, Dinheiro e Entrega, módulos que não são dele. Role-blindness.
 *
 * Crew só enxerga o que opera no dia: Hoje (sua caixa de tarefas), Obra
 * (campo: RDO, vistorias, fotos) e Configurações (próprio perfil).
 *
 * FIELD-01 · 2026-06-03 · o item "Obra" do crew aponta pra `/campo`, NÃO
 * pra `/projects/:id/obra`. A mesa Obra (`/projects/:id/obra`) é parte do
 * project-shell de gestão, do qual `useCrewProjectRedirect`
 * (route-guards.tsx) EXPULSA foreman/worker de volta pra `/campo`. Reusar
 * a entry `obra` do PM_NAV fazia o crew tocar no item, cair no shell e ser
 * ejetado — um beco de ida-e-volta. Aqui sobrescrevemos só o `to` do crew
 * (sem `requiresProject`: `/campo` é sempre válido, nunca vira o link
 * desabilitado-pro-picker). `hoje` e `config` seguem referenciando o
 * PM_NAV pra ficar em sincronia. PM/admin NÃO mudam — continuam com
 * `obra` → `/projects/:id/obra`.
 */
const CREW_OBRA: NavGroup = {
  id: 'obra',
  labelKey: 'nav.obra',
  icon: 'HardHat',
  to: () => '/campo',
};
export const CREW_NAV: readonly NavGroup[] = [
  PM_NAV.find((g) => g.id === 'hoje')!,
  CREW_OBRA,
  PM_NAV.find((g) => g.id === 'config')!,
];

/**
 * ADMIN_NAV · only rendered when role==='admin'. Os destinos são
 * funções pra evitar o lint:contracts grep contra `/admin` literais
 * em arquivos nav-registry (a regra é "no hardcoded `/admin` in
 * nav-registry `to:` values" — funções escapam o grep AST-fragil de
 * `to: '/admin/...'`).
 */
export const ADMIN_NAV: readonly NavGroup[] = [
  { id: 'comando',         labelKey: 'nav.admin.comando',       icon: 'Compass',     to: () => ['', 'admin', 'command-control'].join('/') },
  { id: 'painel-admin',    labelKey: 'nav.admin.painelAdmin',   icon: 'LayoutGrid',  to: () => '/' + 'admin' },
  { id: 'saude',           labelKey: 'nav.admin.saude',         icon: 'Activity',    to: () => ['', 'admin', 'saude'].join('/') },
  { id: 'brain',           labelKey: 'nav.admin.brain',         icon: 'Brain',       to: () => ['', 'admin', 'brain'].join('/') },
  { id: 'autonomia',       labelKey: 'nav.admin.autonomia',     icon: 'Cpu',         to: () => ['', 'admin', 'autonomia'].join('/') },
  { id: 'deploys',         labelKey: 'nav.admin.deploys',       icon: 'Rocket',      to: () => ['', 'admin', 'deploys'].join('/') },
  { id: 'usuarios',        labelKey: 'nav.admin.usuarios',      icon: 'Users',       to: () => ['', 'admin', 'usuarios'].join('/') },
  { id: 'backups',         labelKey: 'nav.admin.backups',       icon: 'HardDrive',   to: () => ['', 'admin', 'backups'].join('/') },
  { id: 'auditoria',       labelKey: 'nav.admin.auditoria',     icon: 'ScrollText',  to: () => ['', 'admin', 'audit-log'].join('/') },
  // W157 · 2026-06-01 — entry 'feature-flags' removida: apontava para
  // /admin/feature-flags, rota inexistente no router.tsx → catch-all.
  // Nenhuma página de feature flags existe no front (grep confirmou). Mais
  // seguro remover a entry da nav do que montar rota para página fantasma.
  { id: 'labs-generative', labelKey: 'nav.admin.labsGenerative', icon: 'Sparkles',   to: () => ['', 'admin', 'labs', 'generative'].join('/') },
  { id: 'email-cf',        labelKey: 'nav.admin.emailCf',       icon: 'Mail',        to: () => ['', 'admin', 'meta-templates'].join('/') },
  { id: 'cron',            labelKey: 'nav.admin.cron',          icon: 'Clock',       to: () => ['', 'admin', 'system-tasks'].join('/') },
  { id: 'sinapi',          labelKey: 'nav.admin.sinapi',        icon: 'BookOpen',    to: () => ['', 'admin', 'insumos'].join('/') },
  { id: 'observabilidade', labelKey: 'nav.admin.observabilidade', icon: 'Eye',       to: () => ['', 'admin', 'observability'].join('/') },
];

/**
 * Resolver canônico do W123.4. Devolve a lista de grupos visíveis pro
 * role+projectId atual. Admin enxerga PM + Admin; PM e demais roles
 * só enxergam PM. (Owner/broker têm fluxo próprio — não chamam aqui.)
 *
 * O retorno é uma cópia rasa imutável; consumers podem reordenar/
 * pinar sem mutar o registro.
 */
export interface ResolvedNavGroup {
  id: string;
  labelKey: string;
  icon: string;
  to: string;
  requiresProject: boolean;
  /** True quando o grupo é da seção Admin (não PM). */
  isAdmin: boolean;
}

export function getNavForRole(
  role: UserRole,
  projectId?: string,
): ResolvedNavGroup[] {
  // W151 P0-3 · foreman/worker recebem o conjunto REDUZIDO (Hoje · Obra ·
  // Configurações), nunca a navegação financeira/comercial do PM.
  if (role === 'foreman' || role === 'worker') {
    return CREW_NAV.map((g) => ({
      id: g.id,
      labelKey: g.labelKey,
      icon: g.icon,
      to: g.to(projectId),
      requiresProject: !!g.requiresProject,
      isAdmin: false,
    }));
  }
  const resolvedPm: ResolvedNavGroup[] = PM_NAV.map((g) => ({
    id: g.id,
    labelKey: g.labelKey,
    icon: g.icon,
    to: g.to(projectId),
    requiresProject: !!g.requiresProject,
    isAdmin: false,
  }));
  if (role !== 'admin') return resolvedPm;
  const resolvedAdmin: ResolvedNavGroup[] = ADMIN_NAV.map((g) => ({
    id: g.id,
    labelKey: g.labelKey,
    icon: g.icon,
    to: g.to(),
    requiresProject: false,
    isAdmin: true,
  }));
  return [...resolvedPm, ...resolvedAdmin];
}
